/* eslint-disable */



import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import CopyCell from './components/CopyCell';


const API_BASE_URL = 'https://api.medstown.com/api/medshubs/v1/admin';


// Modal Portal Component
const ModalPortal = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return ReactDOM.createPortal(
    children,
    document.body
  );
};


const ApiClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(20);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedClient, setSelectedClient] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [newCredentials, setNewCredentials] = useState(null);


  const [formData, setFormData] = useState({
    provider_name: '',
    provider_email: '',
    provider_company: '',
    ip_whitelist: [],
    expires_at: ''
  });


  useEffect(() => {
    fetchClients();
  }, [currentPage, statusFilter]);


  const fetchClients = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        size: pageSize,
        ...(statusFilter && { status: statusFilter })
      };
      const response = await axios.get(`${API_BASE_URL}/providers`, { params });
      setClients(response.data.data.providers);
      setTotalPages(response.data.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching clients:', error);
      alert('Failed to fetch API clients');
    } finally {
      setLoading(false);
    }
  };


  const fetchAnalytics = async (clientId, days = 7) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/providers/${clientId}/analytics`,
        { params: { days } }
      );
      setAnalytics(response.data.data);
      setShowAnalytics(true);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      alert('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };


  const handleCreateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/providers`, formData);
      setNewCredentials(response.data.data);
      alert('API Client created successfully! Please save the credentials shown.');
      setShowModal(false);
      resetForm();
      fetchClients();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to create API client');
    } finally {
      setLoading(false);
    }
  };


  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/providers/${selectedClient._id}`,
        formData
      );
      alert('API Client updated successfully!');
      setShowModal(false);
      resetForm();
      fetchClients();
    } catch (error) {
      alert('Failed to update API client');
    } finally {
      setLoading(false);
    }
  };


  const handleStatusChange = async (clientId, newStatus) => {
    setLoading(true);
    try {
      await axios.patch(`${API_BASE_URL}/providers/${clientId}/status`, {
        status: newStatus
      });
      alert('Status updated successfully!');
      fetchClients();
    } catch (error) {
      alert('Failed to update status');
    } finally {
      setLoading(false);
    }
  };


  const handleRegenerateKeys = async (clientId) => {
    if (!confirm('Are you sure you want to regenerate API keys? The old keys will stop working immediately.')) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/providers/${clientId}/regenerate`
      );
      setNewCredentials(response.data.data);
      fetchClients();
    } catch (error) {
      alert('Failed to regenerate keys');
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteClient = async (clientId) => {
    if (!confirm('Are you sure you want to delete this API client? This action cannot be undone.')) return;
    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/providers/${clientId}`);
      alert('API Client deleted successfully!');
      fetchClients();
    } catch (error) {
      alert('Failed to delete API client');
    } finally {
      setLoading(false);
    }
  };


  const openModal = (type, client = null) => {
    setModalType(type);
    setSelectedClient(client);
    if (client && type === 'edit') {
      setFormData({
        provider_name: client.provider_name,
        provider_email: client.provider_email,
        provider_company: client.provider_company || '',
        ip_whitelist: client.ip_whitelist || [],
        expires_at: client.expires_at ? new Date(client.expires_at).toISOString().slice(0, 16) : ''
      });
    }
    setShowModal(true);
  };


  const resetForm = () => {
    setFormData({
      provider_name: '',
      provider_email: '',
      provider_company: '',
      ip_whitelist: [],
      expires_at: ''
    });
    setSelectedClient(null);
  };


  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">API Client Management</h1>
              <p className="text-gray-600 mt-1">Manage API consumers and monitor usage</p>
            </div>
            <button
              onClick={() => openModal('create')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add New Client
            </button>
          </div>
        </div>


        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex gap-4 items-center">
            <label className="text-sm font-medium text-gray-700">Filter by Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Clients</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="revoked">Revoked</option>
            </select>
            <div className="ml-auto text-sm text-gray-600">
              Total: <span className="font-semibold">{clients.length}</span> clients
            </div>
          </div>
        </div>


        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex flex-col justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading clients...</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-20">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No API clients</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new API client.</p>
              <div className="mt-6">
                <button
                  onClick={() => openModal('create')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add New Client
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Details</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {clients.map((client) => (
                      <tr key={client._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{client.provider_name}</div>
                          <div className="text-sm text-gray-500">{client.provider_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {client.provider_company || <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
  <CopyCell apiKey={client.api_key} />
</td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={client.status}
                            onChange={(e) => handleStatusChange(client._id, e.target.value)}
                            className={`text-xs font-semibold px-3 py-1 rounded-full border-0 cursor-pointer ${
                              client.status === 'active' ? 'bg-green-100 text-green-800' :
                              client.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}
                          >
                            <option value="active">Active</option>
                            <option value="suspended">Suspended</option>
                            <option value="revoked">Revoked</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(client.createdAt).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => fetchAnalytics(client._id)}
                              className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50"
                              title="View Analytics"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => openModal('edit', client)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="Edit Client"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRegenerateKeys(client._id)}
                              className="text-orange-600 hover:text-orange-900 p-1 rounded hover:bg-orange-50"
                              title="Regenerate API Keys"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteClient(client._id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                              title="Delete Client"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>


              {/* Pagination */}
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex items-center px-4 py-2 text-sm text-gray-700">
                    {currentPage} / {totalPages}
                  </div>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>


        {/* Create/Edit Modal */}
        {showModal && (
          <ModalPortal>
            <div 
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
            >
              <div 
                className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {modalType === 'create' ? 'Add New API Client' : 'Edit API Client'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <form onSubmit={modalType === 'create' ? handleCreateClient : handleUpdateClient} className="p-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.provider_name}
                        onChange={(e) => setFormData({...formData, provider_name: e.target.value})}
                        placeholder="Enter client or developer name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <input
                        type="email"
                        required
                        disabled={modalType === 'edit'}
                        value={formData.provider_email}
                        onChange={(e) => setFormData({...formData, provider_email: e.target.value})}
                        placeholder="client@example.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      {modalType === 'edit' && (
                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed after creation</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Organization / Company</label>
                      <input
                        type="text"
                        value={formData.provider_company}
                        onChange={(e) => setFormData({...formData, provider_company: e.target.value})}
                        placeholder="Company or organization name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">IP Whitelist</label>
                      <input
                        type="text"
                        value={formData.ip_whitelist.join(', ')}
                        onChange={(e) => setFormData({
                          ...formData, 
                          ip_whitelist: e.target.value.split(',').map(ip => ip.trim()).filter(Boolean)
                        })}
                        placeholder="192.168.1.1, 10.0.0.1 (comma-separated)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty to allow all IPs</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Access Expiration</label>
                      <input
                        type="datetime-local"
                        value={formData.expires_at}
                        onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for no expiration</p>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      {loading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      )}
                      {loading ? 'Processing...' : modalType === 'create' ? 'Create Client' : 'Update Client'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </ModalPortal>
        )}


        {/* New Credentials Modal */}
        {newCredentials && (
          <ModalPortal>
            <div 
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
              onClick={() => setNewCredentials(null)}
            >
              <div 
                className="bg-white rounded-lg max-w-2xl w-full shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 bg-green-50">
                  <div className="flex items-center gap-3">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h2 className="text-2xl font-bold text-green-900">API Credentials Generated</h2>
                  </div>
                </div>
                <div className="p-6">
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                    <div className="flex">
                      <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className="text-sm text-yellow-800">
                        <strong>Important:</strong> Save these credentials now. The API Secret will not be shown again.
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="font-mono text-sm">{newCredentials.provider_name}</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">API Key (Public)</label>
                      <div className="bg-gray-50 p-3 rounded-lg flex justify-between items-center">
                        <code className="font-mono text-sm break-all">{newCredentials.api_key}</code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(newCredentials.api_key);
                            alert('API Key copied!');
                          }}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                          title="Copy to clipboard"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">API Secret (Private)</label>
                      <div className="bg-red-50 p-3 rounded-lg border border-red-200 flex justify-between items-center">
                        <code className="font-mono text-sm break-all text-red-900">{newCredentials.api_secret}</code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(newCredentials.api_secret);
                            alert('API Secret copied!');
                          }}
                          className="ml-2 text-red-600 hover:text-red-800"
                          title="Copy to clipboard"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={() => setNewCredentials(null)}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      I've Saved the Credentials
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </ModalPortal>
        )}


        {/* Analytics Modal */}
        {showAnalytics && analytics && (
          <ModalPortal>
            <div 
              className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
              style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
              onClick={() => setShowAnalytics(false)}
            >
              <div 
                className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-2xl font-bold text-gray-900">Client API Usage Analytics</h2>
                  <button
                    onClick={() => setShowAnalytics(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-blue-600 font-medium mb-1">Total API Calls</div>
                          <div className="text-3xl font-bold text-blue-900">{analytics.total_stats.totalRequests.toLocaleString()}</div>
                        </div>
                        <svg className="w-12 h-12 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-green-600 font-medium mb-1">Avg Response Time</div>
                          <div className="text-3xl font-bold text-green-900">{analytics.total_stats.avgResponseTime.toFixed(0)}<span className="text-lg">ms</span></div>
                        </div>
                        <svg className="w-12 h-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-lg border border-red-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-red-600 font-medium mb-1">Error Count</div>
                          <div className="text-3xl font-bold text-red-900">{analytics.total_stats.totalErrors}</div>
                        </div>
                        <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-gray-600">
                      <strong>Reporting Period:</strong> {new Date(analytics.period.from).toLocaleDateString('en-IN')} - {new Date(analytics.period.to).toLocaleDateString('en-IN')} ({analytics.period.days} days)
                    </p>
                  </div>
                  {analytics.daily_stats.length > 0 && (
                    <div className="overflow-x-auto">
                      <h3 className="text-lg font-semibold mb-3">Daily Breakdown</h3>
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-4 py-2 text-left">Date</th>
                            <th className="px-4 py-2 text-left">Endpoint</th>
                            <th className="px-4 py-2 text-right">Requests</th>
                            <th className="px-4 py-2 text-right">Avg Time (ms)</th>
                            <th className="px-4 py-2 text-right">Errors</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {analytics.daily_stats.map((stat, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-2">{stat._id.date}</td>
                              <td className="px-4 py-2 font-mono text-xs">{stat._id.endpoint}</td>
                              <td className="px-4 py-2 text-right font-medium">{stat.count}</td>
                              <td className="px-4 py-2 text-right">{stat.avgResponseTime.toFixed(2)}</td>
                              <td className="px-4 py-2 text-right">
                                <span className={`${stat.errors > 0 ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                                  {stat.errors}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ModalPortal>
        )}
      </div>
    </div>
  );
};


export default ApiClientManagement;